<script setup lang="ts">
import NamaIcon from "@/components/icons/NamaIcon.vue";
import {dashboardPresenterKey, DashboardWidgetProps} from "@/utils/presenters/interfaces.ts";
import {computed, inject, ref, toRef} from "vue";
import Translator from "@/utils/common/Translator.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import {useDashboardViewOptionsStore} from "@/stores/dashboardViewOptionsStore.ts";
import IntegerValueFormatter from "@/utils/UIConstants/AGGridEditViewValueFormatters/IntegerValueFormatter.ts";
import BIActionMenu from "@/components/bi/BIActionMenu.vue";
import {buildDrillDownByClick, buildDrillDownClick, executeLink} from "@/components/bi/biInteractionHelpers.ts";
import {useBIFilterBadges} from "@/components/bi/useBIFilterBadges.ts";
import {useBIInteractionData} from "@/components/bi/useBIInteractionData.ts";
import type {
  BIDrillDownByClick,
  BIDrillDownByDimension,
  BIDrillDownClick,
  BIDrillDownPointValues,
  BIDrillDownTarget,
  BILinkPointValues,
  BILinkTarget,
  CrossFilterEmission,
} from "@/components/bi/biChartTypes.ts";
import WizardSlotSelectorButton from "@/components/bi/WizardSlotSelectorButton.vue";
import BIWidgetRuntimeSelectorInfo = namaTypes.BIWidgetRuntimeSelectorInfo;
import DTOMetricsCardLine = namaTypes.DTOMetricsCardLine;

const props = defineProps<DashboardWidgetProps>();
const emit = defineEmits<{
  crossFilterClicked: [emissions: CrossFilterEmission[]];
  drillDownRequested: [click: BIDrillDownClick];
  drillDownByRequested: [click: BIDrillDownByClick];
}>();

const widgetId: string = (props.chartLine.widgetId ?? 'metrics-card');
const metricsCardItems = computed<DTOMetricsCardLine[]>(() => props.chartLine.metricsCards ?? [])
const heading = computed(() => Translator.calcByLanguage(props.chartLine.arChartTitle, props.chartLine.enChartTitle));

const dashboardPresenter = inject(dashboardPresenterKey, undefined);
const runtimeSelectorInfo = computed<BIWidgetRuntimeSelectorInfo | undefined>(
  () => (props.chartLine as any)?.runtimeSelectorInfo);

async function onSlotOverrideChanged() {
  if (props.chartLine.widgetId)
    await dashboardPresenter?.redrawWidgets(props.chartLine.widgetId);
}

// --- BI interaction (click-emit, drill-down, links, drill-down-by) ---
const chartLineRef = toRef(props, 'chartLine');
const {
  clickEmitData, drillTargets, drillFilterMetadata, drillPoints,
  linkTargets, linkPoints, drillByDimensions, drillByData,
} = useBIInteractionData(chartLineRef);

const appliedCrossFilterCodes = computed<string[]>(() => (props.chartLine as any).appliedCrossFilterCodes ?? []);
const {isFilterSource, isFilteredByOther, filteredByTooltip, emittingTooltip} =
  useBIFilterBadges(() => widgetId, appliedCrossFilterCodes);

const hasClickEmit = computed(() => clickEmitData.value.length > 0);
const hasMenu = computed(() =>
  drillTargets.value.length > 0 || linkTargets.value.length > 0 || drillByDimensions.value.length > 0,
);

function onCardClick(rowIdx: number, event: MouseEvent) {
  if (!hasClickEmit.value) return; // falls through to anchor href
  event.preventDefault();
  const emissions: CrossFilterEmission[] = [];
  for (const mapping of clickEmitData.value) {
    const point = mapping.points?.[rowIdx];
    if (point && Object.keys(point).length > 0) {
      emissions.push({
        crossFilterCode: mapping.crossFilterCode,
        sourceChartId: mapping.sourceChartId ?? widgetId,
        dataIndex: rowIdx,
        ...point,
      });
    }
  }
  if (emissions.length) emit('crossFilterClicked', emissions);
}

// --- Context menu state ---
interface MenuState {
  rowIdx: number;
  x: number;
  y: number;
}
const menu = ref<MenuState | undefined>();
const menuOpen = computed<boolean>({
  get: () => !!menu.value,
  set: (v) => { if (!v) menu.value = undefined; },
});

function openMenu(rowIdx: number, event: MouseEvent) {
  event.preventDefault();
  event.stopPropagation();
  if (!hasMenu.value) return;
  menu.value = {rowIdx, x: event.clientX, y: event.clientY};
}

const menuPointValues = computed<BIDrillDownPointValues>(
  () => (menu.value ? drillPoints.value[menu.value.rowIdx] : undefined) ?? {},
);
const menuLinkPointValues = computed<BILinkPointValues>(
  () => (menu.value ? linkPoints.value[menu.value.rowIdx] : undefined) ?? {},
);

function selectDrillTarget(target: BIDrillDownTarget) {
  if (!menu.value) return;
  const sourceWidgetCode = (props.chartLine as any).widgetCode;
  const click = buildDrillDownClick(target, menuPointValues.value, drillFilterMetadata.value,
    sourceWidgetCode, widgetId);
  if (click) emit('drillDownRequested', click);
  menu.value = undefined;
}

function selectLink(link: BILinkTarget) {
  if (!menu.value) return;
  const pv = menuLinkPointValues.value[link.key];
  if (pv) executeLink(link, pv);
  menu.value = undefined;
}

function selectDrillByDimension(dim: BIDrillDownByDimension) {
  if (!menu.value) return;
  const sourceWidgetCode = (props.chartLine as any).widgetCode;
  const click = buildDrillDownByClick(dim, drillByData.value, menu.value.rowIdx,
    sourceWidgetCode, widgetId, props.dashboardId);
  if (click) emit('drillDownByRequested', click);
  menu.value = undefined;
}

interface PercentStyles {
  background?: string,
  text?: string,
  border?: string,
  hover?: string,
}

const NEUTRAL_PERCENT_STYLES: PercentStyles = {
  background: "dark:tw-bg-gray-400 tw-bg-gray-500",
  hover: "hover:tw-bg-gray-500/10 dark:hover:tw-bg-gray-500/10",
};

const POSITIVE_PERCENT_STYLES: PercentStyles = {
  background: "tw-bg-green-500",
  text: "tw-text-green-500",
  border: "tw-border-b-8 tw-border-b-green-500",
  hover: "hover:tw-bg-green-500/10 dark:hover:tw-bg-green-500/10",
};

const NEGATIVE_PERCENT_STYLES: PercentStyles = {
  background: "tw-bg-red-500",
  text: "tw-text-red-500",
  border: "tw-border-t-8 tw-border-t-red-500",
  hover: "hover:tw-bg-red-500/10 dark:hover:tw-bg-red-500/10",
};

function getPercentStyles(line: DTOMetricsCardLine): PercentStyles {
  if (!canShowPercent(line)) return NEUTRAL_PERCENT_STYLES;
  return line.badgePercent > 0 ? POSITIVE_PERCENT_STYLES : NEGATIVE_PERCENT_STYLES;
}

function canShowPercent(line?: DTOMetricsCardLine): boolean {
  const percent = line?.badgePercent;
  return !ObjectChecker.isNullOrUndefined(percent) && percent !== 0;
}

function toPercent(line?: DTOMetricsCardLine): string {
  if (!canShowPercent(line)) return '';
  const percent = line.badgePercent;
  const sign = percent > 0 ? "+" : "";
  return `${sign}${Number(percent).toFixed(1)}%`;
}

function canShowIcon(item: DTOMetricsCardLine): boolean {
  return ObjectChecker.isNotEmptyOrNull(item.iconCode);
}

function canShowSubtitle(item: DTOMetricsCardLine) {
  const text = Translator.calcByLanguage(item.subtitleAr, item.subtitleEn);
  return ObjectChecker.isNotEmptyOrNull(text);
}

const viewOptionsStore = useDashboardViewOptionsStore();
const isAdmin = computed(() => LoginUtils.isCurrentUserAdminOrTreatAsAdmin());
const showAdminToolbox = computed(() => isAdmin.value && viewOptionsStore.showToolbox);

function openWidgetEditView() {
  if (!widgetId) return;
  const link = PlaceUtils.editLink('DashBoardWidget', widgetId);
  if (link) window.open(link, '_blank');
}
</script>

<template>
  <div :key="chartKey + widgetId"
       class="tw-h-full tw-w-full tw-overflow-y-auto dark:tw-bg-namaBackground-bright-dark tw-bg-namaBackground-bright-light tw-relative"
       :class="{'bi-widget-source': isFilterSource, 'bi-widget-filtered': isFilteredByOther}">
    <div v-if="isFilterSource || isFilteredByOther"
         class="tw-absolute tw-top-1 tw-end-1 tw-z-10 tw-flex tw-items-center tw-gap-1">
      <div v-if="isFilterSource"
           class="tw-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-primary tw-text-white tw-shadow"
           :title="emittingTooltip">
        <q-icon name="filter_alt" size="14px" class="emit-funnel"/>
      </div>
      <div v-if="isFilteredByOther"
           class="tw-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-info tw-text-white tw-shadow"
           :title="filteredByTooltip">
        <q-icon name="filter_alt" size="14px"/>
      </div>
    </div>
    <a v-if="showAdminToolbox"
       class="tw-absolute tw-z-20 tw-bg-gray-200 active:tw-bg-gray-300 dark:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-1 tw-rounded-lg tw-cursor-pointer tw-shadow"
       :class="(isFilterSource || isFilteredByOther) ? 'tw-top-7' : 'tw-top-1'"
       style="inset-inline-end: 0.25rem;"
       :title="'Open widget edit screen (admin)'"
       @click.stop="openWidgetEditView">
      <q-icon name="open_in_new" size="18px"/>
    </a>
    <div v-if="heading || (runtimeSelectorInfo && props.chartLine.widgetId)"
         class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-p-3 md:tw-mb-4">
      <span v-if="heading"
            v-text="heading"
            class="tw-font-bold tw-text-2xl dark:tw-text-white"/>
      <div class="tw-flex tw-items-center tw-gap-1 tw-shrink-0">
        <WizardSlotSelectorButton v-if="runtimeSelectorInfo && props.chartLine.widgetId"
                                  :widget-id="props.chartLine.widgetId"
                                  :info="runtimeSelectorInfo"
                                  @applied="onSlotOverrideChanged"
                                  @reset="onSlotOverrideChanged"/>
      </div>
    </div>
    <div class="tw-flex tw-flex-wrap tw-p-3 tw-max-h-full ">
      <div
        v-for="(item, rowIdx) in metricsCardItems"
        :key="rowIdx"
        data-animate-box
        class="tw-grow tw-flex tw-overflow-hidden tw-min-w-fit tw-w-full mobile:tw-basis-1/2 tw-basis-1/4 tw-p-1 tw-min-h-16">
        <a :href="hasClickEmit ? undefined : item.link"
           @click="onCardClick(rowIdx, $event)"
           @contextmenu="openMenu(rowIdx, $event)"
           class="dark:tw-bg-namaBackground-bright-dark tw-bg-namaBackground-bright-light tw-ring-1 tw-ring-black/10 dark:tw-ring-white/10 tw-rounded-md tw-w-full tw-h-full tw-cursor-pointer tw-relative"
           :class="getPercentStyles(item).hover">
          <button v-if="hasMenu"
                  type="button"
                  class="bi-card-kebab tw-absolute tw-top-1 tw-end-1 tw-rounded hover:tw-bg-black/10 dark:hover:tw-bg-white/10 tw-p-0.5"
                  @click.stop.prevent="openMenu(rowIdx, $event)">
            <q-icon name="more_vert" size="16px"/>
          </button>
          <div
            class="tw-flex tw-flex-col tw-justify-center tw-p-3 tw-min-w-full tw-capitalize tw-h-full tw-relative">
            <span class="tw-absolute tw-h-full tw-w-1.5 tw-rounded-s-md tw-left-0" :class="getPercentStyles(item).background"/>
            <div class="tw-flex tw-items-center tw-gap-2 tw-justify-between">
              <div class="tw-text-start tw-space-y-1 dark:tw-text-white">
                <p data-animate-text v-html="Translator.calcByLanguage(item.headerAr,item.headerEn)"
                   class="tw-text-sm tw-font-normal tw-text-black/75 dark:tw-text-white/75"/>
                <div data-animate-text class="tw-flex tw-items-end tw-gap-1" v-if="!!item.value">
                  <p v-text="IntegerValueFormatter.instance().format({format:item.numberFormat},item.value)"
                     class="tw-text-3xl tw-leading-none tw-font-semibold tw-text-black dark:tw-text-white"/>
                  <p v-text="item.suffix" class="tw-text-md tw-text-black/70 dark:tw-text-white/70"/>
                </div>
              </div>
              <div class="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-justify-center tw-shrink-0" dir="ltr">
                <NamaIcon data-animate-icon class="tw-size-5"
                          :defaultIcon="item.iconCode" v-if="canShowIcon(item)"/>
                <div class="tw-flex tw-justify-between tw-items-center tw-gap-1 tw-rounded-md tw-p-0.5 tw-bg-red">
                  <p v-text="toPercent(item)" :class="getPercentStyles(item).text"/>
                  <span class="tw-block tw-border-x-4 tw-border-x-transparent" :class="getPercentStyles(item).border"/>
                </div>
              </div>
            </div>
            <template v-if="canShowSubtitle(item)">
              <q-separator class="tw-my-2 dark:tw-bg-white/10"/>
              <p data-animate-text v-html="Translator.calcByLanguage(item.subtitleAr,item.subtitleEn)"
                 class="tw-text-black/50 dark:tw-text-white/50 tw-text-xs tw-normal-case"/></template>
          </div>
        </a>
      </div>
    </div>
    <BIActionMenu
      v-model="menuOpen"
      :targets="drillTargets"
      :point-values="menuPointValues"
      :filter-metadata="drillFilterMetadata"
      :drill-down-by-dimensions="drillByDimensions"
      :link-targets="linkTargets"
      :link-point-values="menuLinkPointValues"
      :x="menu?.x ?? 0"
      :y="menu?.y ?? 0"
      @drill-down-selected="selectDrillTarget"
      @drill-down-by-selected="selectDrillByDimension"
      @link-selected="selectLink"/>
  </div>
</template>

<style scoped>
.bi-widget-source {
  box-shadow: inset 0 0 0 2px var(--q-primary);
}

.bi-widget-filtered {
  box-shadow: inset 0 0 0 1px var(--q-info);
}

.bi-widget-source.bi-widget-filtered {
  box-shadow: inset 0 0 0 2px var(--q-primary), inset 0 0 0 4px var(--q-info);
}

.emit-funnel {
  transform: rotate(180deg);
}

.bi-card-kebab {
  z-index: 1;
}
</style>
